/**
 * Where the process output goes.
 *
 * Three persist modes:
 *  - "table"          — write into a raw target table (auto-created on first
 *                       run; insert, or upsert when key columns are set);
 *  - "transaction"    — map the output onto an existing TRANSACTION object's
 *                       header + detail forms. Rows are grouped by the unique-
 *                       transaction-id column(s); each group is saved atomically
 *                       as ONE transaction through the runtime engine, so the
 *                       transaction's validations/business logic apply.
 *  - "business_logic" — the process's own function does the writing.
 *  - "none"           — a PROVIDER: the process only previews; its rows are
 *                       pulled into another form's lines (a line source, run
 *                       server-side by the transaction engine). No Execute.
 *
 * Independently of the mode, a process may declare PROCESS-level business
 * logic: one function called ONCE with every row about to be persisted. In
 * table/transaction mode it runs BEFORE the write (pre-processing — it may
 * return a replacement row set, block, or warn); in business_logic mode it IS
 * the write. Same declaration shape as a transaction's pre-commit hook, so the
 * transaction designer's editor edits it verbatim.
 *
 * The target database defaults to the project database; picking an external
 * connection is tucked behind "Advanced" (table mode only — transactions
 * always live in the project database).
 *
 * An ANALYTICS connection (Dremio) is chosen exactly like any other target —
 * that is the whole point — and the one extra choice it brings, the storage
 * format, appears only once such a connection is selected. Iceberg behaves
 * like a normal table (insert, or upsert on key columns); the parquet/CSV
 * file formats are rewritten whole on every run, so key columns are turned
 * off with the reason shown rather than silently ignored.
 */

import { useEffect, useMemo, useState } from 'react'
import { Code2, Pencil, Trash2, Wand2 } from 'lucide-react'
import {
  fetchConnectionTypes, fetchLineSourceUsage, fetchProcessTransactions,
  type LineSourceUse,
} from '@/api/genprocess'
import type { ItemChange } from '@/pages/designers/transaction/types'
import { itemChangeSummary, normalizeItemChange } from '@/pages/designers/transaction/types'
import ItemChangeCodeEditor from '@/pages/designers/transaction/ItemChangeCodeEditor'
import type {
  ColumnType, ConnectionTypeInfo, ExternalConnection, GenProcess, TargetColumn,
  TransactionTarget, TxnCatalogEntry, TxnFieldMap, TxnForm, WriteCapability,
} from './types'
import {
  Field, SectionHeader, emptyHintStyle, hintStyle, inputStyle, selectStyle,
  tdStyle, thStyle,
} from './ui'
import TargetMapping from './vendor/TargetMapping'

interface Props {
  project: string
  process: GenProcess
  /** Columns the last preview produced (source + calculated) — drives pickers. */
  availableColumns: string[]
  connections: ExternalConnection[]
  onChange: (patch: Partial<GenProcess>) => void
  /** Deep-link a business_logic function name to the source code editor. */
  onOpenCode?: (method: string) => void
}

export default function TargetPanel({
  project, process, availableColumns, connections, onChange, onOpenCode,
}: Props) {
  const mode = process.persist_mode === 'transaction' ? 'transaction'
    : process.persist_mode === 'business_logic' ? 'business_logic'
      : process.persist_mode === 'none' ? 'none' : 'table'

  return (
    <div>
      <LineSourceUsageNote project={project} objName={process.obj_name || ''} />
      <SectionHeader>Where do the results go?</SectionHeader>
      <div style={{ display: 'flex', gap: 18, marginBottom: 14 }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="radio" checked={mode === 'table'}
            onChange={() => onChange({ persist_mode: 'table' })}
            style={{ marginTop: 3 }}
          />
          <span>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Database table</div>
            <div style={hintStyle}>
              Insert / update rows in a target table (auto-created if needed).
            </div>
          </span>
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="radio" checked={mode === 'transaction'}
            onChange={() => onChange({ persist_mode: 'transaction' })}
            style={{ marginTop: 3 }}
          />
          <span>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Transaction</div>
            <div style={hintStyle}>
              Create records of an existing transaction object — header +
              detail forms, with its validations and business logic.
            </div>
          </span>
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="radio" checked={mode === 'business_logic'}
            onChange={() => onChange({ persist_mode: 'business_logic' })}
            style={{ marginTop: 3 }}
          />
          <span>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Business logic</div>
            <div style={hintStyle}>
              A function receives all the rows and does the processing itself —
              for work no single table or transaction describes.
            </div>
          </span>
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="radio" checked={mode === 'none'}
            onChange={() => onChange({ persist_mode: 'none' })}
            style={{ marginTop: 3 }}
          />
          <span>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Line source only</div>
            <div style={hintStyle}>
              Nothing is written — the rows are offered to another form&apos;s
              lines (Line Entry). The process has no Execute button.
            </div>
          </span>
        </label>
      </div>

      {mode === 'table' ? (
        <TableTarget
          process={process}
          availableColumns={availableColumns}
          connections={connections}
          onChange={onChange}
        />
      ) : mode === 'transaction' ? (
        <TransactionTargetEditor
          project={project}
          process={process}
          availableColumns={availableColumns}
          onChange={onChange}
        />
      ) : mode === 'none' ? (
        <div style={emptyHintStyle}>
          This process only previews. A transaction&apos;s Line Entry pulls its rows
          in (source kind &quot;General Process&quot;), running it on the server with the
          record&apos;s values as criteria and the user&apos;s own <em>run</em> right on
          this process.
        </div>
      ) : (
        <div style={emptyHintStyle}>
          The function below does the writing. It receives every row the user
          executes and reports back what it did — nothing else is written.
        </div>
      )}

      <LineSourceSection project={project} process={process} mode={mode} onChange={onChange} />

      {mode !== 'none' && <ProcessLogicSection
        process={process}
        mode={mode}
        availableColumns={availableColumns}
        project={project}
        onChange={onChange}
        onOpenCode={onOpenCode}
      />}
    </div>
  )
}

// ── line source (provider) ─────────────────────────────────────────────────

/** "Used as a line source by …" — the transaction line forms whose Line Entry
    pulls rows from this process. Shown in every mode (any process can feed a
    line form), so changing a process its consumers rely on is never blind. */
function LineSourceUsageNote({ project, objName }: { project: string; objName: string }) {
  const [uses, setUses] = useState<LineSourceUse[]>([])
  useEffect(() => {
    let cancelled = false
    if (!objName) { setUses([]); return }
    fetchLineSourceUsage(project, objName)
      .then((u) => { if (!cancelled) setUses(u) })
      .catch((err) => {
        console.warn('Could not read where this process is used as a line source', err)
        if (!cancelled) setUses([])
      })
    return () => { cancelled = true }
  }, [project, objName])
  if (!uses.length) return null
  return (
    <div style={{ ...hintStyle, marginBottom: 12 }}>
      <strong>Used as a line source by:</strong>{' '}
      {uses.map((u) => `${u.form_title} (${u.obj_name}${u.source_label ? ` · ${u.source_label}` : ''})`)
        .join(', ')}
    </div>
  )
}

/** How the process behaves when a transaction's Line Entry calls it — in ANY
    mode: it only returns its rows (the target above is never written from a
    transaction), scoped by the row security chosen here when one is chosen. */
function LineSourceSection({ project, process, mode, onChange }: {
  project: string
  process: GenProcess
  mode: string
  onChange: (patch: Partial<GenProcess>) => void
}) {
  const [catalog, setCatalog] = useState<TxnCatalogEntry[]>([])
  useEffect(() => {
    let cancelled = false
    fetchProcessTransactions(project)
      .then(({ transactions }) => { if (!cancelled) setCatalog(transactions) })
      .catch((err) => {
        console.warn('Could not load the transaction catalogue', err)
        if (!cancelled) setCatalog([])
      })
    return () => { cancelled = true }
  }, [project])
  const current = process.row_security_from?.object || ''
  return (
    <div style={{ marginTop: 16 }}>
      <SectionHeader>When a transaction uses it for its lines</SectionHeader>
      {mode !== 'none' && (
        <div style={{ ...hintStyle, marginBottom: 8 }}>
          Called from a transaction&apos;s Line Entry, this process only returns its rows —
          nothing is written to the target above. Run on its own it saves as usual.
        </div>
      )}
      <Field label="Row security from">
        <select
          style={selectStyle}
          value={current}
          onChange={(e) => onChange({
            row_security_from: e.target.value ? { object: e.target.value } : undefined,
          })}
        >
          <option value="">— none (the source SQL is the only scope) —</option>
          {catalog.map((t) => (
            <option key={t.obj_name} value={t.obj_name}>{t.obj_name}</option>
          ))}
        </select>
      </Field>
      <div style={hintStyle}>
        When set, the rows a user may pull are filtered by this transaction&apos;s row
        security for them — so the source SQL must return the columns it filters on
        (e.g. BRANCH_CODE); a process that cannot be scoped is refused, never run
        unscoped. When not set, no row security applies.
      </div>
    </div>
  )
}

// ── process-level business logic ───────────────────────────────────────────

/**
 * The one function that sees the WHOLE row set. Declared exactly like a
 * transaction's pre-commit hook — so the transaction designer's own editor
 * (mode, method, language, spec / Call-API endpoint) edits it unchanged.
 */
function ProcessLogicSection({
  process, mode, availableColumns, project, onChange, onOpenCode,
}: {
  process: GenProcess
  mode: 'table' | 'transaction' | 'business_logic'
  availableColumns: string[]
  project: string
  onChange: (patch: Partial<GenProcess>) => void
  onOpenCode?: (method: string) => void
}) {
  const [editorOpen, setEditorOpen] = useState(false)
  const ic = normalizeItemChange(process.business_logic as ItemChange | undefined)
  const declared = ic?.mode === 'business_logic' || ic?.mode === 'rest_api'
  const blMethod = ic?.mode === 'business_logic' ? (ic.business_logic?.method || '') : ''
  const owns = mode === 'business_logic'

  return (
    <div style={{ marginTop: 22, borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
      <SectionHeader>
        {owns ? 'Business logic (does the writing)' : 'Business logic (before the write)'}
      </SectionHeader>
      <div style={hintStyle}>
        {owns
          ? 'Called once with every row being executed. It does its own writes; '
            + 'the process writes nothing else. Whatever it returns as a message '
            + 'is what the user is told when the run finishes.'
          : 'Optional. Called once with every row about to be written, before '
            + 'anything is written — to derive or correct values (it may return '
            + 'a replacement row set), check across rows, or write to other '
            + 'tables. It can block the run, raise warnings the user may accept, '
            + 'and report a closing message.'}
      </div>

      {declared ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          marginTop: 10, padding: 10, borderRadius: 6,
          border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
        }}>
          <span style={{
            fontSize: 13, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
            background: 'var(--color-accent)', color: '#fff',
          }}>
            {ic?.mode === 'rest_api' ? 'Call API' : 'Business Logic'}
          </span>
          <span style={{ ...hintStyle, marginTop: 0, flex: 1, minWidth: 120 }}>
            {itemChangeSummary(process.business_logic as ItemChange)}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditorOpen(true)}>
            <Pencil size={13} /> Edit
          </button>
          {ic?.mode === 'business_logic' && onOpenCode && (
            <button className="btn btn-ghost btn-sm" onClick={() => onOpenCode(blMethod)}>
              <Code2 size={13} /> Code
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }}
            onClick={() => onChange({ business_logic: undefined })}
            title={owns
              ? 'Removing it leaves this process with nothing to run'
              : 'Remove the pre-write function'}
          >
            <Trash2 size={13} /> Remove
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditorOpen(true)}>
            Set business logic
          </button>
          {owns && (
            <span style={{ ...hintStyle, marginTop: 0, color: '#f5a524' }}>
              Required for this target — the process cannot run without it.
            </span>
          )}
        </div>
      )}

      <ItemChangeCodeEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        value={declared ? ic : null}
        initialMode="business_logic"
        title="Process Business Logic"
        helpText={
          'Runs ONCE with every row being executed — `rows` in the payload is '
          + 'the whole set, not one row. Return a message to report the result, '
          + 'errors/warnings to block or ask, and (before the write) a '
          + 'replacement `rows` set to change what gets persisted.'
        }
        formColumnNames={availableColumns}
        headerColumnNames={[]}
        project={project}
        onOpenCode={onOpenCode}
        onSave={(v) => { onChange({ business_logic: v }); setEditorOpen(false) }}
      />
    </div>
  )
}

// ── table mode ─────────────────────────────────────────────────────────────

const FORMAT_LABELS: Record<string, string> = {
  iceberg: 'Iceberg table (recommended)',
  parquet: 'Parquet file (replaced each run)',
  csv: 'CSV file (replaced each run)',
}

/** What the chosen target connection accepts, or null for an ordinary
    database (which simply writes a native table — no choice to make). */
function useWriteCapability(
  connectionId: string | undefined,
  connections: ExternalConnection[],
): WriteCapability | null {
  const [types, setTypes] = useState<ConnectionTypeInfo[]>([])
  const dbType = useMemo(() => {
    const id = (connectionId || '').trim()
    if (!id) return ''   // project database
    const conn = connections.find((c) => c.CONNECTION_ID === id)
    return (conn?.DATABASE_TYPE || '').trim().toLowerCase()
  }, [connectionId, connections])

  // Only ask the catalogue once a non-project connection is actually chosen —
  // the overwhelmingly common case never pays for the request.
  useEffect(() => {
    if (!dbType || types.length) return
    let alive = true
    fetchConnectionTypes()
      .then((t) => { if (alive) setTypes(t) })
      .catch(() => { /* no capability info → treat as an ordinary database */ })
    return () => { alive = false }
  }, [dbType, types.length])

  return useMemo(() => {
    if (!dbType) return null
    return types.find((t) => t.value === dbType)?.writeback ?? null
  }, [dbType, types])
}

function TableTarget({
  process, availableColumns, connections, onChange,
}: Omit<Props, 'project'>) {
  const [showAdvanced, setShowAdvanced] = useState(
    !!(process.target_connection_id || '').trim())
  const columns = process.target_columns || []
  const keys = process.key_columns || []

  const writeback = useWriteCapability(process.target_connection_id, connections)
  const formats = writeback?.formats || []
  const format = (process.target_format || '').trim() || formats[0] || ''
  // A file format has no rows to match against — it is dropped and rewritten.
  const keysDisabledReason = (writeback && format && format !== 'iceberg')
    ? `${FORMAT_LABELS[format] || format} targets are replaced on every run, `
      + 'so there is nothing to match key columns against.'
    : ''

  const setColumns = (next: TargetColumn[]) => onChange({ target_columns: next })

  const deriveFromPreview = () => {
    if (!availableColumns.length) return
    const have = new Set(columns.map((c) => (c.source || c.name).toLowerCase()))
    const added = availableColumns
      .filter((c) => !have.has(c.toLowerCase()))
      .map((c) => ({ name: c.toLowerCase(), type: 'string' as ColumnType, source: c.toLowerCase() }))
    if (added.length) setColumns([...columns, ...added])
  }

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Target table">
          <input
            value={process.target_table || ''}
            onChange={(e) => onChange({ target_table: e.target.value })}
            style={inputStyle}
            placeholder="auto-created on first run if missing"
          />
        </Field>
        <div style={{ alignSelf: 'end', marginBottom: 10 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setShowAdvanced((s) => !s)}
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced…'}
          </button>
        </div>
      </div>

      {showAdvanced && (
        <Field label="Target database (advanced)">
          <select
            value={process.target_connection_id || ''}
            onChange={(e) => onChange({ target_connection_id: e.target.value })}
            style={selectStyle}
          >
            <option value="">Project database (default)</option>
            {connections.map((c) => (
              <option key={c.CONNECTION_ID} value={c.CONNECTION_ID}>
                {c.CONNECTION_NAME || c.CONNECTION_ID} ({c.DATABASE_TYPE || 'postgresql'})
              </option>
            ))}
          </select>
          <div style={hintStyle}>
            External connections are managed in the workbench Advanced tab.
            Leave on “Project database” unless this process must write into
            another application's database or an analytics store.
          </div>
        </Field>
      )}

      {writeback && (
        <Field label="Storage format">
          <select
            value={format}
            onChange={(e) => {
              const next = e.target.value
              // Moving to a replace-only format drops key columns rather than
              // leaving them set but inoperative.
              onChange(next === 'iceberg'
                ? { target_format: next }
                : { target_format: next, key_columns: [] })
            }}
            style={selectStyle}
          >
            {formats.map((f) => (
              <option key={f} value={f}>{FORMAT_LABELS[f] || f}</option>
            ))}
          </select>
          <div style={hintStyle}>
            {format === 'iceberg' ? (
              <>
                Written as a table: rows are appended, or updated in place when
                key columns are set. The target path must sit in a{' '}
                <strong>writable</strong> source (S3, HDFS, Nessie/Arctic or
                $scratch).
              </>
            ) : (
              <>
                Written as a file: the target is <strong>dropped and
                rewritten</strong> with this run's rows, so each run fully
                replaces the last. Choose the Iceberg table format to append or
                update instead.
              </>
            )}
            {' '}No <code>gp_id</code> key column is created on an analytics
            target — Iceberg has no auto-increment.
          </div>
        </Field>
      )}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <SectionHeader>Column mapping ({columns.length})</SectionHeader>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
          onClick={deriveFromPreview}
          title="Add a mapping row for every column the preview produced"
          disabled={!availableColumns.length}>
          <Wand2 size={13} /> From preview columns
        </button>
      </div>

      {/* Editability + per-column lookups are defined in the Preferences tab
          (one place for every persist mode), so the grid's own Editable column
          is hidden here. */}
      <TargetMapping
        columns={columns}
        keyColumns={keys}
        onChangeColumns={setColumns}
        onChangeKeys={(next) => onChange({ key_columns: next })}
        columnEditing={false}
        keysDisabledReason={keysDisabledReason}
      />

      <div style={hintStyle}>
        {keysDisabledReason ? (
          <>{keysDisabledReason} Choose the Iceberg table format if re-runs
          should update rows in place.</>
        ) : (
          <>
            Tick <strong>Key</strong> on the columns identifying a row to make
            re-runs idempotent (update-in-place). With no keys, every run
            inserts fresh rows.
          </>
        )}
        {' '}Choose which columns are <strong>editable</strong> (and attach 🔍
        lookups) in the <strong>Preferences</strong> tab.
      </div>
    </>
  )
}

// ── transaction mode ───────────────────────────────────────────────────────

function blankTxnTarget(): TransactionTarget {
  return {
    obj_name: '', group_by: [], header_fields: [], detail_fields: {},
    apply_item_change: false,
  }
}

function TransactionTargetEditor({
  project, process, availableColumns, onChange,
}: {
  project: string
  process: GenProcess
  availableColumns: string[]
  onChange: (patch: Partial<GenProcess>) => void
}) {
  const [catalog, setCatalog] = useState<TxnCatalogEntry[]>([])
  const [loaded, setLoaded] = useState(false)
  const tt = process.transaction_target || blankTxnTarget()

  useEffect(() => {
    let cancelled = false
    fetchProcessTransactions(project)
      .then(({ transactions }) => { if (!cancelled) { setCatalog(transactions); setLoaded(true) } })
      .catch(() => { if (!cancelled) setLoaded(true) })
    return () => { cancelled = true }
  }, [project])

  const entry = useMemo(
    () => catalog.find((t) => t.obj_name === tt.obj_name),
    [catalog, tt.obj_name])
  const headerForm = entry?.forms.find((f) => f.is_header)
  const detailForms = (entry?.forms || []).filter((f) => !f.is_header)

  const set = (patch: Partial<TransactionTarget>) =>
    onChange({ transaction_target: { ...tt, ...patch } })

  const setHeaderFields = (fields: TxnFieldMap[]) => set({ header_fields: fields })
  const setDetailFields = (formNo: string, fields: TxnFieldMap[]) => {
    const next = { ...tt.detail_fields }
    if (fields.length) next[formNo] = fields
    else delete next[formNo]
    set({ detail_fields: next })
  }

  const autoMap = (form: TxnForm): TxnFieldMap[] => {
    const bySrc = new Map(availableColumns.map((c) => [c.toLowerCase(), c.toLowerCase()]))
    const out: TxnFieldMap[] = []
    for (const col of form.columns) {
      const hit = bySrc.get(col.form_key) || bySrc.get(col.db_name.toLowerCase())
      if (hit) out.push({ target: col.form_key, source: hit })
    }
    return out
  }

  const toggleGroupBy = (col: string) => {
    const c = col.toLowerCase()
    set({
      group_by: tt.group_by.includes(c)
        ? tt.group_by.filter((g) => g !== c)
        : [...tt.group_by, c],
    })
  }

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Target transaction">
          <select
            value={tt.obj_name}
            onChange={(e) => onChange({
              transaction_target: { ...blankTxnTarget(), obj_name: e.target.value },
            })}
            style={selectStyle}
          >
            <option value="">— pick a transaction object —</option>
            {catalog.map((t) => (
              <option key={t.obj_name} value={t.obj_name}>{t.obj_name}</option>
            ))}
          </select>
          {loaded && catalog.length === 0 && (
            <div style={hintStyle}>
              This project has no transaction models yet — design a
              transaction first.
            </div>
          )}
        </Field>
        <Field label="Options">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13.5, marginTop: 6 }}>
            <input
              type="checkbox"
              checked={!!tt.apply_item_change}
              onChange={(e) => set({ apply_item_change: e.target.checked })}
            />
            Apply item-change defaults on insert (engine seeding)
          </label>
        </Field>
      </div>

      {entry && (
        <>
          <SectionHeader>Unique transaction id — group rows by</SectionHeader>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
            {availableColumns.length === 0 && (
              <div style={emptyHintStyle}>
                Run a preview first so the output columns are known.
              </div>
            )}
            {availableColumns.map((c) => {
              const on = tt.group_by.includes(c.toLowerCase())
              return (
                <button
                  key={c}
                  onClick={() => toggleGroupBy(c)}
                  className="btn btn-sm"
                  style={{
                    padding: '2px 9px', fontSize: 13, fontFamily: 'monospace',
                    border: '1px solid var(--color-border)', borderRadius: 10,
                    background: on ? 'var(--color-accent)' : 'transparent',
                    color: on ? '#fff' : 'var(--color-text)',
                  }}
                >
                  {c}
                </button>
              )
            })}
          </div>
          <div style={hintStyle}>
            Rows sharing the same value(s) merge into ONE transaction: the
            header is taken from the group's first row, and every row becomes
            a detail line. Leave empty to save the whole result as a single
            transaction.
          </div>

          {headerForm && (
            <FieldMappingGrid
              title={`Header — ${headerForm.form_title}`}
              form={headerForm}
              fields={tt.header_fields}
              availableColumns={availableColumns}
              onChange={setHeaderFields}
              onAutoMap={() => setHeaderFields(autoMap(headerForm))}
            />
          )}

          {detailForms.map((f) => (
            <FieldMappingGrid
              key={f.form_no}
              title={`Detail ${f.form_no} — ${f.form_title} (one row per source row)`}
              form={f}
              fields={tt.detail_fields[f.form_no] || []}
              availableColumns={availableColumns}
              onChange={(fields) => setDetailFields(f.form_no, fields)}
              onAutoMap={() => setDetailFields(f.form_no, autoMap(f))}
            />
          ))}

          <div style={hintStyle}>
            Unmapped form columns fall back to the transaction's own defaults
            (auto-generated keys, default values, audit columns), and the
            header's key is linked into detail rows automatically. Designed
            validations and business logic run on every generated transaction.
          </div>
        </>
      )}
    </>
  )
}

function FieldMappingGrid({
  title, form, fields, availableColumns, onChange, onAutoMap,
}: {
  title: string
  form: TxnForm
  fields: TxnFieldMap[]
  availableColumns: string[]
  onChange: (fields: TxnFieldMap[]) => void
  onAutoMap: () => void
}) {
  const mapped = new Map(fields.map((f) => [f.target, f.source]))

  const setMapping = (formKey: string, source: string) => {
    const next = fields.filter((f) => f.target !== formKey)
    if (source) next.push({ target: formKey, source })
    onChange(next)
  }

  return (
    <div style={{ marginTop: 14 }}>
      <SectionHeader
        right={
          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
            onClick={onAutoMap} disabled={!availableColumns.length}
            title="Map every form column whose name matches an output column">
            <Wand2 size={13} /> Auto-map
          </button>
        }
      >
        {title}
      </SectionHeader>
      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Form column</th>
              <th style={thStyle}>Maps from (source column)</th>
              <th style={thStyle}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {form.columns.map((col) => (
              <tr key={col.form_key}>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 600 }}>{col.heading}</span>
                  <span style={{ opacity: 0.55, marginLeft: 6, fontSize: 13, fontFamily: 'monospace' }}>
                    {col.form_key}
                  </span>
                </td>
                <td style={tdStyle}>
                  <select
                    value={mapped.get(col.form_key) || ''}
                    onChange={(e) => setMapping(col.form_key, e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">— not mapped —</option>
                    {availableColumns.map((a) => (
                      <option key={a} value={a.toLowerCase()}>{a}</option>
                    ))}
                    {(() => {
                      const cur = mapped.get(col.form_key)
                      return cur && !availableColumns.some((a) => a.toLowerCase() === cur)
                        ? <option value={cur}>{cur}</option> : null
                    })()}
                  </select>
                </td>
                <td style={{ ...tdStyle, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {[
                    col.key ? 'key' : '',
                    col.auto ? 'auto-generated' : '',
                    col.mandatory ? 'mandatory' : '',
                    col.col_type,
                  ].filter(Boolean).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
